import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/supabase/types';
import { isWithinWindow, resolveWindow, windowStatus } from '@/lib/availability/resolveWindow';
import type { Anchor, EventTimes } from '@/lib/availability/types';
import { isWithinUpgradeWindow } from '@/lib/utils/upgrade-window';
import { classifyEnrollmentsTestAccess } from '@/lib/tests/enrollmentsRequiringTest';

/**
 * Helpers for the "your ticket does not include the test" notice. The notice
 * exists only while at least one of the event's tests is open: before that
 * there is nothing to take, and after it there is nothing left to act on, so
 * it disappears without a trace.
 */

export type TestWindowRow = {
  available_from: string | null;
  available_from_anchor: Anchor | null;
  available_until: string | null;
  available_until_anchor: Anchor | null;
};

/**
 * Is any of these tests open right now, and until when? `until` is the latest
 * close among the open tests, or null when one of them never closes.
 */
export function openTestsWindow(
  tests: ReadonlyArray<TestWindowRow>,
  event: EventTimes,
  now: Date,
): { open: boolean; until: Date | null } {
  let open = false;
  let until: Date | null = null;
  let unbounded = false;
  for (const t of tests) {
    const input = {
      from: t.available_from,
      from_anchor: t.available_from_anchor ?? 'absolute',
      until: t.available_until,
      until_anchor: t.available_until_anchor ?? 'absolute',
    };
    if (windowStatus(input, event, now) !== 'open') continue;
    open = true;
    const closes = resolveWindow(input, event).until;
    if (!closes) unbounded = true;
    else if (!until || closes > until) until = closes;
  }
  return { open, until: unbounded ? null : until };
}

export type UpgradeCandidateTicket = {
  id: string;
  content_id: string;
  price: number;
  max_quantity: number | null;
  sold_quantity: number | null;
  sale_start_date: string | null;
  sale_end_date: string | null;
  sale_start_anchor: Anchor | null;
  sale_end_anchor: Anchor | null;
};

/**
 * Could the participant switch to this certificate ticket right now? Mirrors
 * the filter in ChangeTicketModal (sale window, or a strict upgrade inside the
 * post-event upgrade window) plus sold-out, so the notice never offers an
 * upgrade the modal would not list.
 */
export function isCertificateUpgradeOpen(
  ticket: UpgradeCandidateTicket,
  ctx: { currentTicketId: string | null; paidAmount: number; event: EventTimes; now: Date },
): boolean {
  if (ticket.id === ctx.currentTicketId) return false;
  if (ticket.max_quantity !== null && (ticket.sold_quantity ?? 0) >= ticket.max_quantity) return false;
  const eventEnded = !!ctx.event.end_date && new Date(ctx.event.end_date) < ctx.now;
  if (eventEnded && isWithinUpgradeWindow(ctx.event.end_date, ctx.now) && ticket.price > ctx.paidAmount) return true;
  return isWithinWindow(
    {
      from: ticket.sale_start_date,
      from_anchor: ticket.sale_start_anchor ?? 'absolute',
      until: ticket.sale_end_date,
      until_anchor: ticket.sale_end_anchor ?? 'absolute',
    },
    ctx.event,
    ctx.now,
  );
}

/**
 * The content ids (among `candidates`) that have a certificate ticket the
 * participant can upgrade to now. A read error yields no upgrades: the notice
 * then falls back to "contact the organizer", which is always true.
 */
export async function fetchCertificateUpgradeContentIds(
  client: Pick<SupabaseClient<Database>, 'from'>,
  candidates: ReadonlyArray<{ contentId: string; currentTicketId: string | null; paidAmount: number; event: EventTimes }>,
  now: Date,
): Promise<Set<string>> {
  const result = new Set<string>();
  const contentIds = Array.from(new Set(candidates.map((c) => c.contentId)));
  if (contentIds.length === 0) return result;

  const { data, error } = await client
    .from('tickets')
    .select('id, content_id, price, max_quantity, sold_quantity, sale_start_date, sale_end_date, sale_start_anchor, sale_end_anchor')
    .in('content_id', contentIds)
    .eq('allow_certificate', true)
    .eq('status', 'available')
    .eq('requires_promocode', false);
  if (error || !data) return result;

  for (const c of candidates) {
    const open = (data as UpgradeCandidateTicket[]).some(
      (t) => t.content_id === c.contentId && isCertificateUpgradeOpen(t, { ...c, now }),
    );
    if (open) result.add(c.contentId);
  }
  return result;
}

export interface TestNotIncludedItem {
  enrollmentId: string;
  contentId: string;
  contentTitle: string;
  contentType: 'course' | 'event';
  ticket: { id: string; name: string; price: number } | null;
  paidAmount: number;
  /** ISO; null when an open test never closes. */
  openUntil: string | null;
  canUpgrade: boolean;
  organizerEmail: string | null;
}

type EnrollmentRow = {
  id: string;
  content_id: string;
  ticket_id: string | null;
  paid_amount: number | null;
  refund_status: string | null;
  ticket: { id: string; name: string; price: number } | null;
  content: {
    id: string;
    title: string;
    type: 'course' | 'event';
    status: string;
    start_date: string | null;
    end_date: string | null;
    organizer_profiles: { email: string | null } | null;
  } | null;
};

const CHUNK = 100;

/**
 * Every enrollment of `userId` (optionally on one content) whose ticket does
 * not include the test while one of that event's tests is open. The single
 * source for the notice on the event page, My tests and the dashboard.
 * Speaker tickets and refunded registrations never appear. Throws on a read
 * error; callers show no notice.
 */
export async function fetchTestNotIncludedItems(
  client: Pick<SupabaseClient<Database>, 'from' | 'rpc'>,
  { userId, contentId }: { userId: string; contentId?: string },
  now: Date,
): Promise<TestNotIncludedItem[]> {
  let query = client
    .from('enrollments')
    .select(`
      id, content_id, ticket_id, paid_amount, refund_status,
      ticket:tickets ( id, name, price ),
      content:content ( id, title, type, status, start_date, end_date, organizer_profiles ( email ) )
    `)
    .eq('user_id', userId)
    .not('ticket_id', 'is', null);
  if (contentId) query = query.eq('content_id', contentId);
  const { data, error } = await query;
  if (error) throw new Error(`fetchTestNotIncludedItems: enrollments lookup failed: ${error.message}`);

  const rows = ((data ?? []) as unknown as EnrollmentRow[]).filter(
    (e) => e.content && e.content.status === 'published' && e.refund_status !== 'completed',
  );
  if (rows.length === 0) return [];

  const access = await classifyEnrollmentsTestAccess(client, rows);
  const excluded = rows.filter((e) => access.get(e.id) === 'not_included');
  if (excluded.length === 0) return [];

  const contentIds = Array.from(new Set(excluded.map((e) => e.content_id)));
  const testsByContent = new Map<string, TestWindowRow[]>();
  for (let i = 0; i < contentIds.length; i += CHUNK) {
    const { data: tests, error: testsError } = await client
      .from('tests')
      .select('content_id, available_from, available_from_anchor, available_until, available_until_anchor')
      .in('content_id', contentIds.slice(i, i + CHUNK))
      // A hidden test is not takeable, so it never opens the notice.
      .eq('is_active', true);
    if (testsError) throw new Error(`fetchTestNotIncludedItems: tests lookup failed: ${testsError.message}`);
    for (const t of (tests ?? []) as Array<TestWindowRow & { content_id: string }>) {
      const list = testsByContent.get(t.content_id) ?? [];
      list.push(t);
      testsByContent.set(t.content_id, list);
    }
  }

  const open = excluded
    .map((e) => {
      const event = { start_date: e.content!.start_date, end_date: e.content!.end_date };
      return { e, event, window: openTestsWindow(testsByContent.get(e.content_id) ?? [], event, now) };
    })
    .filter((x) => x.window.open);
  if (open.length === 0) return [];

  const upgradable = await fetchCertificateUpgradeContentIds(
    client,
    open.map(({ e, event }) => ({ contentId: e.content_id, currentTicketId: e.ticket_id, paidAmount: e.paid_amount ?? 0, event })),
    now,
  );

  return open.map(({ e, window }) => ({
    enrollmentId: e.id,
    contentId: e.content_id,
    contentTitle: e.content!.title,
    contentType: e.content!.type,
    ticket: e.ticket,
    paidAmount: e.paid_amount ?? 0,
    openUntil: window.until ? window.until.toISOString() : null,
    canUpgrade: upgradable.has(e.content_id),
    organizerEmail: e.content!.organizer_profiles?.email ?? null,
  }));
}
